import { CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ApplyColumnStateParams, ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { cloneDeep, reduce, startsWith } from 'lodash';
import {
    AgGridOptionsBuilder,
    AgGridFilterParams,
    AgGridColumns,
    AgGridDropdownCellEditor,
} from 'src/app/Compliance/AgGrid';
import { WeissmanDateFormat } from 'src/app/UI-Lib/Pipes/Date-Format/date-formatting.pipe';
import {
    FinalizeCheckImportGridActionCellRendererComponent,
    ICellRendererParamsForFinalizeCheckImportGridAction,
} from './agGridActionCellRenderer.component';
import { DropdownCellEditorParams } from 'src/app/Compliance/AgGrid/CellEditors/agGridDropdownCellEditor.component';
import { PaymentBatchService } from 'src/app/Payment-Batch/paymentBatch.service';
import { NavigationService } from 'src/app/Layout/navigation.service';

export interface CheckModelUI extends Core.PaymentBatchCheckImportCheckModel {
    isTotal?: boolean;
}

@Component({
    selector: 'finalize-check-import-checks-grid',
    template: `
        <h3>Imported Checks</h3>
        <ws-ag-grid-angular
            class="ag-theme-balham grid grid-cell-no-focus"
            [gridOptions]="gridOptions"
            [forceShowToolPanel]="true"
            [showConfigureLayout]="false"
            (gridReady)="onAgGridReady($event)"
        >
        </ws-ag-grid-angular>
    `,
    standalone: false,
})
export class FinalizeCheckImportChecksGridComponent {
    constructor(
        private readonly _currencyPipe: CurrencyPipe,
        private readonly _paymentBatchService: PaymentBatchService,
        private readonly _navigationService: NavigationService,
    ) {}

    @Input() checkImportChecks: CheckModelUI[];
    @Input() paymentBatchId: number;
    @Output() editModeChange = new EventEmitter<boolean>();
    @Output() setIsBusy = new EventEmitter<boolean>();
    @Output() reloadGrids = new EventEmitter<void>();
    @Output() checkRefNumChanged = new EventEmitter<string>();

    gridOptions: GridOptions = new AgGridOptionsBuilder({
        suppressScrollOnNewData: true,
        stopEditingWhenCellsLoseFocus: false,
        suppressClickEdit: true,
        onCellEditingStopped: (event) => {
            console.log(event);
            setTimeout(() => {
                if (this.rowBeingEdited) {
                    this._gridApi.setFocusedCell(event.node.rowIndex, 'checkReferenceNum');
                    this._gridApi.startEditingCell({
                        rowIndex: event.node.rowIndex,
                        colKey: 'checkReferenceNum',
                    });
                }
            }, 100);
        },
        rowClassRules: {
            'row-matched': (params) => {
                const gridRow = params.data as CheckModelUI;
                return !!gridRow.paymentCount && (!gridRow.isTotal || startsWith(gridRow.collector, 'Matched'));
            },
            'totals-row': (params) => {
                const gridRow = params.data as CheckModelUI;
                return gridRow.isTotal;
            },
        },
    })
        .withContext(this)
        .withLoadingOverlay()
        .withColumnResize()
        .withMultipleColumnSort()
        .withTextSelection()
        .build();

    refreshing: boolean;
    rowBeingEdited: CheckModelUI;
    enableSaveRow: boolean = false;

    private _gridApi: GridApi;

    onAgGridReady(event: GridReadyEvent): void {
        this._gridApi = event.api;

        const columns: ColDef[] = [
            {
                headerName: 'Payee',
                field: 'collector',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
                cellStyle: (params) => {
                    const row = params.data as CheckModelUI;
                    return row.isTotal ? { textAlign: 'right', fontWeight: 'bold' } : null;
                },
            },
            {
                headerName: 'Check Amount',
                field: 'checkAmount',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                valueFormatter: (x) => this._formatCurrency(x.value),
                cellClass: 'text-align-right',
            },
            {
                headerName: 'Check No',
                field: 'checkNo',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
            },
            {
                headerName: 'Check Date',
                field: 'checkDate',
                width: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as CheckModelUI;
                    if (!(model && model.checkDate)) {
                        return '';
                    }

                    return WeissmanDateFormat(model.checkDate, true, 'stored');
                },
            },
            {
                headerName: 'Payment Count',
                field: 'paymentCount',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
            },
            {
                headerName: 'Check Reference Num',
                field: 'checkReferenceNum',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                editable: true,
                cellEditor: AgGridDropdownCellEditor,
                cellEditorParams: {
                    getOptions: (params: DropdownCellEditorParams) => {
                        const model = params.data as CheckModelUI;
                        if (!model) {
                            return [];
                        }

                        return model.candidateCheckReferenceNums.map((x) => ({ value: x, name: x }));
                    },
                    cellChanged: (params: DropdownCellEditorParams, newValue: string) => {
                        this.checkRefNumChanged.emit(newValue);
                        this.enableSaveRow = true;
                    },
                },
            },
            {
                headerName: 'Unmatched Reason',
                field: 'unmatchedReason',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnLargeWidth,
            },
            {
                headerName: '',
                field: 'actions',
                pinned: 'right',
                width: AgGridColumns.getActionColumnWidth(2),
                minWidth: AgGridColumns.getActionColumnWidth(2),
                maxWidth: AgGridColumns.getActionColumnWidth(2),
                suppressSizeToFit: true,
                suppressAutoSize: true,
                resizable: false,
                suppressColumnsToolPanel: true,
                lockPinned: true,
                sortable: false,
                cellRenderer: FinalizeCheckImportGridActionCellRendererComponent,
                cellRendererParams: {
                    hasEditPermission: () => true,
                    edit: this._edit.bind(this),
                    save: this._save.bind(this),
                    cancel: this._cancel.bind(this),
                    rowBeingEdited: () => this.rowBeingEdited,
                    enableSaveRow: () => this.enableSaveRow,
                } as ICellRendererParamsForFinalizeCheckImportGridAction,
            },
        ];

        const defaultSortModel: ApplyColumnStateParams = {
            state: [{ colId: 'collector', sort: 'asc' }],
            defaultState: { sort: null },
        };

        this._gridApi.setGridOption('columnDefs', columns);
        this._gridApi.applyColumnState(defaultSortModel);
        this._gridApi.sizeColumnsToFit();

        this.setRowData();
    }

    setRowData() {
        if (!(this._gridApi && this.checkImportChecks)) {
            return;
        }

        this._gridApi.setGridOption('rowData', this.checkImportChecks);

        const totalRows = [];
        const matchedPayments = this.checkImportChecks.filter((x) => !!x.paymentCount);

        if (matchedPayments.length) {
            const matchedRow = this._getTotalRow(matchedPayments, true);
            totalRows.push(matchedRow);
        }

        const totalRow = this._getTotalRow(this.checkImportChecks);
        totalRows.push(totalRow);

        this._gridApi.setGridOption('pinnedBottomRowData', totalRows);
    }

    private _formatCurrency(value: number): string {
        if (value == null) {
            return '';
        }

        const formattedVal = this._currencyPipe.transform(Math.abs(value), 'USD', 'symbol-narrow');
        return value < 0 ? `(${formattedVal})` : formattedVal;
    }

    private _getTotalRow(payments: CheckModelUI[], justMatched: boolean = false) {
        const checkAmount = reduce(
            payments,
            (sum, payment) => {
                sum += payment.checkAmount;
                return sum;
            },
            0,
        );

        const paymentCount = reduce(
            payments,
            (sum, payment) => {
                sum += payment.paymentCount;
                return sum;
            },
            0,
        );

        const totalLabel = justMatched ? 'Matched' : 'Total';
        return {
            isTotal: true,
            checkAmount,
            paymentCount,
            collector: `${totalLabel}: ${payments.length}`,
        };
    }

    private _edit(params: ICellRendererParamsForFinalizeCheckImportGridAction) {
        this.rowBeingEdited = cloneDeep(params.data);

        this.editModeChange.emit(!!this.rowBeingEdited);
        params.api.setFocusedCell(params.node.rowIndex, 'checkReferenceNum');
        params.api.startEditingCell({
            rowIndex: params.node.rowIndex,
            colKey: 'checkReferenceNum',
        });
        this._navigationService.enableNavWarning('Editing is in progress. Are you sure you want to leave?');
    }

    private async _save(params: ICellRendererParamsForFinalizeCheckImportGridAction) {
        this.setIsBusy.emit(true);
        const newRow = params.data as CheckModelUI;
        try {
            await this._paymentBatchService.applyManualMatch(
                this.paymentBatchId,
                newRow.paymentBatchCheckImportCheckId,
                newRow.checkReferenceNum,
            );

            this._exitEditMode();
            this.reloadGrids.emit();
        } finally {
            this.setIsBusy.emit(false);
        }
    }

    private _cancel(params: ICellRendererParamsForFinalizeCheckImportGridAction) {
        params.node.setDataValue('checkReferenceNum', this.rowBeingEdited.checkReferenceNum);
        this._exitEditMode();
    }

    private _exitEditMode() {
        this.rowBeingEdited = null;
        this.editModeChange.emit(!!this.rowBeingEdited);
        this.checkRefNumChanged.emit(null);
        this.enableSaveRow = false;
        this._gridApi.stopEditing();
        this._navigationService.disableNavWarning();
    }
}
